// Factory utilities for creating and converting edge objects between internal and API representations.

import FontUtils from '@/services/canvas/fonts/Fonts';
import store from '@/store/store';
import { computed } from 'vue';

const { fontFamilyGetNameByUrl } = FontUtils();

interface FontEntry {
	id: number;
	family: string;
	[key: string]: unknown;
}

interface RawEdgeDesign {
	object_template: string | null;
	border_color: string | null;
	border_style: string | null;
	border_width: number | null;
	shadow_enabled: boolean | null;
	shadow_x: number | null;
	shadow_y: number | null;
	shadow_blur: number | null;
	shadow_color: string | null;
	font_color: string | null;
	font_size: number | null;
	font_url: string | null;
	font_weight: string | null;
	font_style: string | null;
	text_position: string | null;
	text_decoration: string | null;
	line_start: string | null;
	line_start_size: number | null;
	line_end: string | null;
	line_end_size: number | null;
	[key: string]: unknown;
}

interface RawEdgeContent {
	lang: Record<string, Record<string, unknown>>;
}

interface RawEdgeVisibility {
	min_level: number;
	max_level: number;
}

interface RawEdgeNode {
	from_id: number;
	to_id: number;
}

interface RawEdge {
	id: number;
	design: RawEdgeDesign;
	content: RawEdgeContent;
	visibility: RawEdgeVisibility;
	node: RawEdgeNode;
}

interface CanvasNode {
	id: number;
	x: number;
	y: number;
	width: number;
	height: number;
	border: boolean;
	borderWidth: number;
	shape: string;
	hidden: boolean;
	[key: string]: unknown;
}

interface ElementsDesignDefaults {
	edges: Record<string, unknown>;
}

interface ElementsDesign {
	templates: Record<string, Record<string, unknown>>;
	edges: Record<string, unknown>;
	defaults: ElementsDesignDefaults;
}

interface ResolvedEdgeDesign {
	border_color: string;
	border_style: string;
	border_width: number;
	shadow_enabled: boolean;
	shadow_x: number;
	shadow_y: number;
	shadow_blur: number;
	shadow_color: string;
	font_color: string;
	font_size: number;
	font_url: string;
	font_weight: string;
	font_style: string;
	text_position: string;
	text_decoration: string;
	line_start: string;
	line_start_size: number;
	line_end: string;
	line_end_size: number;
	[key: string]: unknown;
}

interface EdgeObject {
	id: number;
	template: string | null;
	fromNodeId: number;
	fromNodeHeight: number;
	fromNodeWidth: number;
	fromNodeBorderWidth: number;
	fromNodeBorder: boolean;
	fromNodeShape: string;
	lineStart: string;
	lineStartSize: number;
	toNodeId: number;
	toNodeHeight: number;
	toNodeWidth: number;
	toNodeBorderWidth: number;
	toNodeBorder: boolean;
	toNodeShape: string;
	lineEnd: string;
	lineEndSize: number;
	startPosX: number;
	endPosX: number;
	startPosY: number;
	endPosY: number;
	border: boolean;
	borderColor: string;
	borderStyle: string;
	borderWidth: number;
	shadow: boolean;
	shadowX: number;
	shadowY: number;
	shadowBlur: number;
	shadowColor: string;
	fontColor: string;
	fontSize: number;
	fontFamily: string | null;
	fontWeight: string;
	fontStyle: string;
	textPosition: string;
	textDecoration: string;
	content: Record<string, unknown>;
	visibilityMin: number;
	visibilityMax: number;
	editing: null;
	hidden: boolean;
}

export const EDGE_PROPS_MAPPING: Record<string, string> = {
	border: 'border_enabled', // todo deprecated -> remove
	borderColor: 'border_color',
	borderStyle: 'border_style',
	borderWidth: 'border_width',
	fontColor: 'font_color',
	fontSize: 'font_size',
	fontWeight: 'font_weight',
	fontStyle: 'font_style',
	fontFamily: 'font_url',
	lineHeight: 'line_height',
	paragraphSpacing: 'paragraph_spacing',
	shadowBlur: 'shadow_blur',
	shadowColor: 'shadow_color',
	shadow: 'shadow_enabled',
	shadowX: 'shadow_x',
	shadowY: 'shadow_y',
	textDecoration: 'text_decoration',
	textPosition: 'text_position',
	textAlign: 'text_align',
	lineStart: 'line_start',
	lineStartSize: 'line_start_size',
	lineEnd: 'line_end',
	lineEndSize: 'line_end_size',
	routingType: 'routing_type',
	visibilityMin: 'min_level',
	visibilityMax: 'max_level',
};

function getValueFromTemplate(
	templateId: string,
	templates: Record<string, Record<string, unknown>>,
	global: Record<string, unknown>,
	property: string,
): unknown {
	if (templateId === '0') return global[property];
	// An edge may reference a template id absent from the map; fall back to null
	// so the caller resolves the default instead of crashing on an undefined
	// template.
	return templates[templateId]?.[property] ?? null;
}

function mappingInverted(): Record<string, string> {
	const invertedMapping: Record<string, string> = {};
	for (const [key, value] of Object.entries(EDGE_PROPS_MAPPING)) {
		Object.assign(invertedMapping, { [value]: key });
	}
	return invertedMapping;
}

export function createEdgeObject(
	edge: RawEdge,
	nodes: CanvasNode[],
	elementsDesign: ElementsDesign,
	fonts: Record<string, FontEntry>,
): EdgeObject {
	const startNode = nodes.find((node) => node.id === edge.node.from_id) as CanvasNode;
	const endNode = nodes.find((node) => node.id === edge.node.to_id) as CanvasNode;
	const edgeDesign = getEdgeDesign(edge, elementsDesign) as ResolvedEdgeDesign;
	// get content for current language because only main language displayed on the map
	const currentLang = computed(() => store.getters['maps/languages/getLanguage'].id as string);
	const contentComputed = edge.content.lang[currentLang.value];
	delete contentComputed.translated;

	return {
		id: edge.id,
		template: edge.design.object_template,
		fromNodeId: edge.node.from_id,
		fromNodeHeight: startNode.height,
		fromNodeWidth: startNode.width,
		fromNodeBorderWidth: startNode.border ? startNode.borderWidth : 0,
		fromNodeBorder: startNode.border,
		fromNodeShape: startNode.shape,
		lineStart: edgeDesign.line_start,
		lineStartSize: edgeDesign.line_start_size,
		toNodeId: edge.node.to_id,
		toNodeHeight: endNode.height,
		toNodeWidth: endNode.width,
		toNodeBorderWidth: endNode.border ? endNode.borderWidth : 0,
		toNodeBorder: endNode.border,
		toNodeShape: endNode.shape,
		lineEnd: edgeDesign.line_end,
		lineEndSize: edgeDesign.line_end_size,
		startPosX: startNode.x,
		endPosX: endNode.x,
		startPosY: startNode.y,
		endPosY: endNode.y,
		border: true,
		borderColor: edgeDesign.border_color,
		borderStyle: edgeDesign.border_style,
		borderWidth: edgeDesign.border_width,
		shadow: edgeDesign.shadow_enabled,
		shadowX: edgeDesign.shadow_x,
		shadowY: edgeDesign.shadow_y,
		shadowBlur: edgeDesign.shadow_blur,
		shadowColor: edgeDesign.shadow_color,
		fontColor: edgeDesign.font_color,
		fontSize: edgeDesign.font_size,
		fontFamily: fontFamilyGetNameByUrl(edgeDesign.font_url, fonts) as string | null,
		fontWeight: edgeDesign.font_weight,
		fontStyle: edgeDesign.font_style,
		textPosition: edgeDesign.text_position,
		textDecoration: edgeDesign.text_decoration,
		routingType: edgeDesign.routing_type ?? 'straight',
		content: contentComputed,
		visibilityMin: edge.visibility.min_level,
		visibilityMax: edge.visibility.max_level,
		editing: null,
		hidden: startNode.hidden || endNode.hidden || false,
	};
}

export function getEdgeDesign(edge: RawEdge, elementsDesign: ElementsDesign): Record<string, unknown> {
	// check design properties of a node if null ->
	// check object template, if "0" -> use global design, if null -> use system defaults
	const { templates, edges: global, defaults: { edges: edgeDefaults } } = elementsDesign;
	let edgeDesign: Record<string, unknown> = {};
	const unlinkedDesign: Record<string, unknown> = JSON.parse(JSON.stringify(edge.design));
	// Iterate over ALL known keys — minimal designs may omit most keys.
	const allKeys = new Set([...Object.keys(edgeDefaults), ...Object.keys(unlinkedDesign)]);
	for (const key of allKeys) {
		const value = key in unlinkedDesign ? unlinkedDesign[key] : null;
		if (value === null) {
			const templateId = edge.design.object_template;
			const templateValue = templateId
				? getValueFromTemplate(templateId, templates, global, key)
				: null;
			const propertyValue =
				templateId && templateValue != null && templateValue !== ''
					? templateValue
					: edgeDefaults[key];
			edgeDesign = Object.assign(edgeDesign, { [key]: propertyValue });
		} else {
			edgeDesign = Object.assign(edgeDesign, { [key]: value });
		}
	}
	return edgeDesign;
}

export function getEdgeDesignFromTemplate(
	templateId: string | null,
	elementsDesign: ElementsDesign,
): Record<string, unknown> {
	const { templates, edges: global, defaults: { edges: edgeDefaults } } = elementsDesign;
	let edgeDesign: Record<string, unknown> = {};
	// if templateId look template[property] -> global[property] -> default[property]
	if (templateId) {
		const template = templateId !== '0' ? templates[templateId] : global;
		// Iterate over ALL default keys to handle minimal templates/globals
		const allKeys = new Set([...Object.keys(edgeDefaults), ...Object.keys(template)]);
		for (const key of allKeys) {
			const value = key in template ? template[key] : null;
			if (value == null) {
				const globalValue = key in global ? global[key] : null;
				Object.assign(edgeDesign, { [key]: globalValue != null ? globalValue : edgeDefaults[key] });
			} else {
				Object.assign(edgeDesign, { [key]: value });
			}
		}
	} else {
		// if templateId is null get values from node default
		edgeDesign = edgeDefaults;
	}

	return edgeDesign;
}

export function getEdgeDesignSyncedToOriginal(
	edgeId: number,
	rawEdges: RawEdge[],
	elementsDesign: ElementsDesign,
	fonts: Record<string, FontEntry>,
): Record<string, unknown> {
	const edge = rawEdges.find((e) => e.id === edgeId) as RawEdge;
	let edgeDesign: Record<string, unknown> = {};
	const edgeOriginalDesign = getEdgeDesign(edge, elementsDesign);
	// collect edge object from related edge
	for (let [key, value] of Object.entries(edgeOriginalDesign)) {
		// convert font_url value ( which is url with id) to fontFamily value;
		if (key === 'font_url' && value) {
			value = fontFamilyGetNameByUrl(value as string, fonts);
		}
		Object.assign(edgeDesign, convertOriginalToEdgeProperty({ [key]: value }));
	}
	return edgeDesign;
}

export function getEdgeDesignInitialComputedValue(
	templateId: string | null,
	elementDesign: ElementsDesign,
	property: string,
): unknown {
	const edgeDesign = getEdgeDesignFromTemplate(templateId, elementDesign);
	return edgeDesign[property];
}

export function getEdgeChangedValues(edge: Record<string, unknown>): Record<string, unknown> {
	let changedValues: Record<string, unknown> = {};
	for (const [property, value] of Object.entries(edge)) {
		// avoid checking non-comparable values
		if (property !== 'object_template' && value !== null) {
			changedValues = Object.assign(changedValues, { [property]: value });
		}
	}
	return changedValues;
}

export function convertToOriginalEdgeProperty(
	edgeProperty: Record<string, unknown>,
): Record<string, unknown> | undefined {
	const [[key, value]] = Object.entries(edgeProperty);
	if (EDGE_PROPS_MAPPING[key]) {
		return { [EDGE_PROPS_MAPPING[key]]: value };
	}
	return undefined;
}

export function convertOriginalToEdgeProperty(
	edgeProperty: Record<string, unknown>,
): Record<string, unknown> {
	const [entries] = Object.entries(edgeProperty);
	const [key, value] = entries;
	const mapping = mappingInverted();

	return { [mapping[key]]: value };
}

export function adaptedOriginalDesignPropertiesToEdge(
	edgeOriginal: Record<string, unknown>,
): Record<string, unknown> {
	const adaptedOriginal: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(EDGE_PROPS_MAPPING)) {
		if (edgeOriginal[value] !== undefined) {
			Object.assign(adaptedOriginal, { [key]: edgeOriginal[value] });
		}
	}
	return adaptedOriginal;
}
